// src/components/LoginGrid.js
import React, { useState } from "react";
import {
    Grid,
    Button,
    TextField,
    Typography,
    Link as MuiLink,
    Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import { ReactComponent as GoogleIcon } from "../resources/google-icon.svg";
import EntremapLogo from "../resources/EntremapLogo.png";import queryString from "query-string";
import { useLocation, Link as RouterLink } from "react-router-dom";
import SignupForm from "./SignupForm";

const useStyles = makeStyles((theme) => ({
    shell: {
        maxWidth: 460,
        margin: "48px auto 120px",
        padding: "0 16px",
        textAlign: "center",
    },
    logo: {
        width: 250,
        height: "auto",
        margin: "0 auto 16px",
        display: "block",
        objectFit: "contain",
    },
    title: {
        fontSize: 36,
        fontWeight: 600,
        marginBottom: 6,
    },
    tagline: {
        letterSpacing: 2,
        fontWeight: 600,
        marginBottom: 18,
        color: "#333",
    },
    field: {
        marginBottom: 14,
    },
    forgotRow: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: -4,
        marginBottom: 12,
    },
    loginBtn: {
        background: "#c3451e",
        color: "#fff",
        fontWeight: 700,
        width: "100%",
        height: 44,
        marginTop: 8,
        "&:hover": { background: "#a63616" },
    },
    dividerText: {
        margin: "18px 0 10px",
        color: "#666",
    },
    providerBtn: {
        width: "100%",
        height: 44,
        background: "#00624d",
        color: "white",
        fontWeight: 700,
        marginTop: 12,
        textTransform: "none",
        "&:hover": { background: "#064f40" },
    },
    providerIconWrap: {
        display: "inline-flex",
        alignItems: "center",
        marginRight: 10,
    },
    qutBtn: {
        background: "#1f1f2b",
        "&:hover": { background: "#191926" },
    },
    footNote: {
        marginTop: 22,
        color: "#333",
    },
    qutCard: {
        marginTop: 16,
        textAlign: "left",
    },
    backLink: {
        marginTop: 12,
        textAlign: "center",
    },
    smallHint: {
        marginTop: theme.spacing(1),
        fontSize: 14,
        textAlign: "center",
    },
}));

const ALLOWED_QUT_DOMAINS = ["qut.edu.au", "connect.qut.edu.au"];
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
const isQutEmail = (s) => {
    const email = String(s || "").trim().toLowerCase();
    if (!isEmail(email)) return false;
    const domain = email.split("@").pop();
    return ALLOWED_QUT_DOMAINS.includes(domain);
};

export default function LoginGrid() {
    const classes = useStyles();
    const { search } = useLocation();
    const query = queryString.parse(search);

    // Use 'redirect' param from App.js (not 'redirectUrl')
    const redirectAfter = query.redirect || query.redirectUrl || "/profile";

    const [view, setView] = useState("main");
    const [email, setEmail] = useState("");
    const [pass, setPass] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    // Handle URL error parameters
    React.useEffect(() => {
        if (query.error === "invalid_domain") {
            setErr("Please sign in with your official @qut.edu.au account.");
        } else if (query.error) {
            setErr("Authentication failed. Please try again.");
        }
    }, [query.error]);

    // QUT Student fields
    const [qutStudentEmail, setQutStudentEmail] = useState("");
    const [qutStudentPassword, setQutStudentPassword] = useState("");
    const [qutNotFound, setQutNotFound] = useState(false);
    const qutStudentEmailInvalid = qutStudentEmail.length > 0 && !isQutEmail(qutStudentEmail);

    // QUT Teacher
    const [qutTeacherEmail, setQutTeacherEmail] = useState("");

    // OAuth functions
    const GoogleSignIn = () => {
        const baseUrl = process.env.NODE_ENV === "production"
            ? ""
            : "http://localhost:5001";
        window.location.href = `${baseUrl}/api/auth/google?redirectUrl=${encodeURIComponent(redirectAfter)}`;
    };

    const LinkedInSignIn = () => {
        const baseUrl = process.env.NODE_ENV === "production"
            ? ""
            : "http://localhost:5001";
        window.location.href = `${baseUrl}/api/auth/linkedin?redirectUrl=${encodeURIComponent(redirectAfter)}`;
    };

    const MicrosoftSignIn = () => {
        const baseUrl = process.env.NODE_ENV === "production"
            ? ""
            : "http://localhost:5001";
        window.location.href = `${baseUrl}/api/auth/microsoft?redirectUrl=${encodeURIComponent(redirectAfter)}`;
    };

    // Email login
    async function onLocalLogin(e) {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const res = await fetch(`/api/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email, password: pass }),
            });
            const data = await res.json();
            if (!res.ok) {
                setErr(data?.message || data?.error || "Login failed");
                return;
            }
            // After successful login, redirect to intended page
            window.location.href = redirectAfter;
        } catch (e2) {
            setErr("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    // QUT Student submit
    async function onQutStudent(e) {
        e.preventDefault();
        setErr("");
        setQutNotFound(false);

        if (!isQutEmail(qutStudentEmail)) return setErr("Please use your QUT email (@qut.edu.au or @connect.qut.edu.au).");
        if (!qutStudentPassword) return setErr("Please enter your password.");

        setLoading(true);
        try {
            const res = await fetch("/api/auth/qut/student", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    email: qutStudentEmail.trim().toLowerCase(),
                    password: qutStudentPassword,
                }),
            });
            const data = await res.json();

            if (res.status === 404) {
                setQutNotFound(true);
                return setErr(data?.message || "No account found for that QUT email.");
            }
            if (!res.ok || data?.success === false) {
                return setErr(data?.message || data?.error || "Login failed");
            }

            const next = data?.redirect || redirectAfter;
            window.location.assign(next);
        } catch {
            setErr("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    // QUT Teacher submit
    async function onQutTeacher(e) {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const res = await fetch("/api/auth/qut/teacher", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: qutTeacherEmail.trim() }),
            });
            const data = await res.json();
            if (!res.ok || data?.success === false) {
                return setErr(data?.message || data?.error || "Login failed");
            }
            const next = data?.redirect || redirectAfter;
            window.location.assign(next);
        } catch {
            setErr("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    // Signup view
    if (view === "signup") {
        return (
            <div className={`${classes.shell} emap-auth-card`}>
                <img className={classes.logo} src={EntremapLogo} alt="Entremap" />
                <Typography className={classes.title}>Entremap</Typography>
                <Typography className={classes.tagline}>Create Account</Typography>

                <SignupForm
                    onBack={() => setView("main")}
                    onLogin={() => setView("main")}
                />

                <div className={classes.backLink}>
                    <MuiLink component="button" onClick={() => setView("main")}>
                        ← Back to login
                    </MuiLink>
                </div>
            </div>
        );
    }

    // Main login view - Email form with other options below
    return (
        <div className={`${classes.shell} emap-auth-card`}>
            <img className={classes.logo} src={EntremapLogo} alt="Entremap" />
            <Typography className={classes.tagline}>Sign in with your email</Typography>

            <form onSubmit={onLocalLogin}>
                <TextField
                    className={classes.field}
                    placeholder="Username or email (case sensitive)"
                    label="Email"
                    type="email"
                    variant="outlined"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                />

                <TextField
                    className={classes.field}
                    placeholder="Password (case sensitive)"
                    label="Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                    autoComplete="current-password"
                    required
                />

                <div className={classes.forgotRow}>
                    <MuiLink href="/contact" color="primary">
                        Need sign-in help?
                    </MuiLink>
                </div>

                {err && (
                    <Typography color="error" style={{ marginBottom: 8 }}>
                        {err}
                    </Typography>
                )}

                <Button className={classes.loginBtn} type="submit" disabled={loading}>
                    {loading ? "LOGGING IN…" : "LOGIN"}
                </Button>
            </form>

            <Typography className={classes.dividerText}>Or login with</Typography>

            {/* Google */}
            <Button
                className={classes.providerBtn}
                onClick={GoogleSignIn}
            >
                <span className={classes.providerIconWrap}>
                    <GoogleIcon width={18} height={18} />
                </span>
                SIGN IN WITH GOOGLE
            </Button>

            {/* LinkedIn */}
            <Button
                className={classes.providerBtn}
                onClick={LinkedInSignIn}
                startIcon={<LinkedInIcon />}
            >
                SIGN IN WITH LINKEDIN
            </Button>

            {/* QUT */}
            <Button
                className={`${classes.providerBtn} ${classes.qutBtn}`}
                onClick={MicrosoftSignIn}
            >
                SIGN IN WITH QUT
            </Button>

            <Typography className={classes.footNote}>
                Not registered yet?{" "}
                <MuiLink component="button" onClick={() => setView("signup")}>
                    signup here
                </MuiLink>
            </Typography>
        </div>
    );
}
